"use client";

import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import { Mail, X, Loader2, ArrowRight } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { ErrorToast, SuccessToast } from "@/components/common/Toast";
import { emailPriceList } from "@/services/home.services";
import { getDeviceId, checkIsLandingPagePath } from "@/_helper/helper";

export function PriceSubscribeDrawer() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const newsletterLoading = useAppSelector(
    (state) => state.home.newsletterLoading,
  );
  const isLandingPageFromRedux = useAppSelector(
    (state) => state.landingPage?.isLandingPage,
  );

  const isLandingPage = Boolean(
    isLandingPageFromRedux || checkIsLandingPagePath(pathname),
  );

  // Check subscription status and auto-open drawer on page visit after short delay
  useEffect(() => {
    if (isLandingPage) {
      setIsOpen(false);
      return;
    }

    const hasSubscribed = sessionStorage.getItem("hasSubscribedPriceDrawer");
    if (hasSubscribed === "true") {
      setIsSubscribed(true);
      return;
    }

    const timer = setTimeout(() => {
      const hasClosed = sessionStorage.getItem("hasClosedPriceDrawer");
      if (!hasClosed) {
        setIsOpen(true);
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [isLandingPage, pathname]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem("hasClosedPriceDrawer", "true");
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const formik = useFormik({
    initialValues: {
      Email: "",
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      Email: Yup.string()
        .required("Email is required")
        .test(
          "is-email-valid",
          "Please enter a valid email address",
          (value) => {
            if (!value) return true;
            const emailRegex =
              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            return emailRegex.test(value);
          },
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        EmailID: values.Email,
        DeviceID: getDeviceId(),
      };
      const response = await dispatch(emailPriceList(payload));
      if (response.meta.requestStatus === "fulfilled") {
        resetForm();
        setIsOpen(false);
        setIsSubscribed(true);
        sessionStorage.setItem("hasSubscribedPriceDrawer", "true");
        SuccessToast("Thank you! Prices emailed successfully.");
      } else {
        ErrorToast("Failed. Please try again.");
      }
    },
  });

  if (isSubscribed || isLandingPage) {
    return null;
  }

  return (
    <>
      {/* Floating Side Trigger Button on Right Edge */}
      {!isOpen && (
        <motion.button
          type="button"
          onClick={handleOpen}
          aria-label="Get prices emailed to you"
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="fixed right-0 top-1/3 -translate-y-1/2 z-[990] bg-[#d4af37] hover:bg-[#c59d5f] text-[#2a2520] py-3 px-2.5 rounded-l-xl shadow-[0_8px_25px_rgba(212,175,55,0.6)] flex flex-col items-center justify-center cursor-pointer border-y border-l border-amber-200 group transition-colors"
        >
          <div className="bg-[#2a2520] text-[#d4af37] p-2 rounded-full shadow-md group-hover:scale-110 transition-transform">
            <Mail className="w-5 h-5" />
          </div>
        </motion.button>
      )}

      {/* Side Drawer Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop / Click-outside overlay (subtle transparent backdrop) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 z-[998] bg-black/20 backdrop-blur-[1px]"
            />

            {/* Main Pop-out Drawer Box */}
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed right-0 top-1/2 -translate-y-1/2 z-[999] w-[310px] sm:w-[360px] bg-white text-[#2a2520] shadow-2xl border border-[#e5dfd5] rounded-l-2xl p-6 sm:p-8 overflow-hidden"
            >
              {/* Close Button Header */}
              <div className="flex justify-between items-center mb-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="bg-[#2a2520] hover:bg-[#3a3530] text-white p-2 rounded-md transition-colors focus:outline-none flex items-center justify-center cursor-pointer"
                  aria-label="Close form"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Heading */}
              <h3
                className="text-2xl sm:text-3xl font-medium text-[#2a2520] mb-6 leading-tight tracking-wide"
                style={{ fontFamily: "var(--font-cormorant-family), serif" }}
              >
                Get the prices emailed to you!
              </h3>

              {/* Form */}
              <form onSubmit={formik.handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="email"
                    name="Email"
                    placeholder="Email"
                    value={formik.values.Email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-none text-gray-800 text-sm font-jost focus:outline-none focus:border-[#5a6f3e] focus:ring-1 focus:ring-[#5a6f3e] transition-colors placeholder:text-gray-400"
                  />
                  {formik.touched.Email && formik.errors.Email && (
                    <p className="text-xs text-red-500 mt-1.5 font-jost">
                      {formik.errors.Email}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={newsletterLoading || formik.isSubmitting}
                  className="w-full bg-[#5a6f3e] hover:bg-[#5a6f3e] text-white text-xs font-semibold uppercase tracking-wider py-3.5 px-4 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed font-jost shadow-md"
                >
                  {newsletterLoading || formik.isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <span>SEND ME THE PRICES</span>
                  )}
                </button>
              </form>

              {/* Subtext */}
              <p className="text-xs text-gray-500 text-center mt-4 font-jost">
                No spam ever, promise!
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
